import React, { useState } from "react";
import { LoadingSpinner } from "@/components/atoms/LoadingSpinner";

// Hooks
import { useCards } from "./hooks/useCards";
import { useDeckManager } from "./hooks/useDeckManager";
import { LocalDeck } from "./types";

// Components
import SearchBar, { ViewMode } from "./components/SearchBar";
import CardBrowser from "./components/CardBrowser";
import MyDecks from "./components/MyDecks";
import DeckCards from "./components/DeckCards";
import CurrentDeck from "./components/CurrentDeck";
import ImportModal from "./components/ImportModal";
import ExportModal from "./components/ExportModal";

const DeckBuilder: React.FC = () => {
  // UI State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string>("all");
  const [selectedRarity, setSelectedRarity] = useState<string>("all");
  const [costRange, setCostRange] = useState<[number, number]>([0, 20]);
  const [selectedElements, setSelectedElements] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [showMyDecks, setShowMyDecks] = useState(false);
  const [showDeckCards, setShowDeckCards] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Custom hooks
  const { cards, loading, getCardImage } = useCards();
  const {
    savedDecks,
    currentDeck,
    editingDeckId,
    setCurrentDeck,
    saveCurrentDeck,
    loadDeck,
    createNewDeck,
    deleteDeck,
    addCardToDeck,
    removeCardFromDeck,
    getCardCount,
    importDeckFromURL,
  } = useDeckManager();

  // Enhanced handlers that manage view state
  const handleCreateNewDeck = () => {
    createNewDeck();
    setShowMyDecks(false);
    setShowDeckCards(false);
  };

  const handleLoadDeck = (deck: LocalDeck) => {
    loadDeck(deck);
    setShowMyDecks(false);
    setShowDeckCards(false);
  };

  const handleImport = (deck: Partial<LocalDeck>) => {
    importDeckFromURL(deck);
    setShowMyDecks(false);
    setShowDeckCards(false);
  };

  const handleLoadPrecon = (precon: {
    name: string;
    avatar?: string;
    spellbook: string[];
    atlas: string[];
    id?: string | undefined;
  }) => {
    setCurrentDeck(precon);
    setShowMyDecks(false);
    setShowDeckCards(false);
  };

  if (loading) {
    return <LoadingSpinner message="Loading cards..." />;
  }

  return (
    <div className="relative h-[calc(100vh-0px)]">
      {/* Search Bar */}
      <SearchBar
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedRarity={selectedRarity}
        setSelectedRarity={setSelectedRarity}
        costRange={costRange}
        setCostRange={setCostRange}
        selectedElements={selectedElements}
        setSelectedElements={setSelectedElements}
        viewMode={viewMode}
        setViewMode={setViewMode}
        showMyDecks={showMyDecks}
        setShowMyDecks={setShowMyDecks}
        showDeckCards={showDeckCards}
        setShowDeckCards={setShowDeckCards}
        savedDecksCount={savedDecks.length}
        onCreateNewDeck={handleCreateNewDeck}
        onImport={() => setShowImportModal(true)}
      />

      {/* Main content */}
      <div className="grid h-[calc(100%-120px)] max-w-[1400px] mx-auto grid-cols-1 lg:grid-cols-[1fr_340px]">
        {/* Left Panel - Card Browser, My Decks, or Deck Cards */}
        <div className="overflow-auto p-4">
          {showMyDecks ? (
            <MyDecks
              savedDecks={savedDecks}
              editingDeckId={editingDeckId}
              cards={cards}
              onLoadDeck={handleLoadDeck}
              onDeleteDeck={deleteDeck}
              onLoadPrecon={handleLoadPrecon}
            />
          ) : showDeckCards ? (
            <DeckCards
              currentDeck={currentDeck}
              cards={cards}
              getCardCount={getCardCount}
            />
          ) : (
            <CardBrowser
              cards={cards}
              searchQuery={searchQuery}
              selectedType={selectedType}
              selectedRarity={selectedRarity}
              costRange={costRange}
              selectedElements={selectedElements}
              viewMode={viewMode}
              onAddCard={addCardToDeck}
              getCardCount={getCardCount}
              getCardImage={getCardImage}
            />
          )}
        </div>

        {/* Right Panel - Current Deck */}
        <CurrentDeck
          currentDeck={currentDeck}
          setCurrentDeck={setCurrentDeck}
          cards={cards}
          editingDeckId={editingDeckId}
          onRemoveCard={removeCardFromDeck}
          onSave={saveCurrentDeck}
          getCardCount={getCardCount}
          onExport={() => setShowExportModal(true)}
        />

        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImport={handleImport}
        />

        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          deck={currentDeck}
          cards={cards}
        />
      </div>
    </div>
  );
};

export default DeckBuilder;
